import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";
import { resolveActiveCompanyId } from "@/lib/activeCompany";

export type ActivityActionType = 
  | "job_created"
  | "job_updated"
  | "job_completed"
  | "job_deleted"
  | "job_cancelled"
  | "poc_started"
  | "poc_completed"
  | "poc_skipped"
  | "pod_started"
  | "pod_completed"
  | "pod_skipped"
  | "appraisal_started"
  | "appraisal_completed"
  | "invoice_generated"
  | "invoice_sent"
  | "invoice_email_sent"
  | "consolidated_invoice_sent"
  | "sms_sent"
  | "tracking_sms_sent"
  | "appraisal_email_sent"
  | "daily_check_completed"
  | "customer_created"
  | "customer_updated"
  | "customer_deleted"
  | "driver_created"
  | "driver_updated"
  | "driver_deleted"
  | "user_invited"
  | "user_removed"
  | "vehicle_created"
  | "vehicle_updated"
  | "vehicle_deleted"
  | "settings_updated"
  | "user_login"
  | "user_logout"
  | "tracking_shared"
  | "integration_connected"
  | "integration_disconnected"
  | "expense_added"
  | "expense_deleted";

interface LogActivityParams {
  actionType: ActivityActionType;
  description: string;
  metadata?: Json;
}

export const useActivityLog = () => {
  const logActivity = useCallback(async ({ actionType, description, metadata = {} }: LogActivityParams) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn("No user found, skipping activity log");
        return;
      }

      // Get user's company_id
      const memberData = { company_id: await resolveActiveCompanyId(user.id) };

      const { error } = await supabase
        .from("user_activity_logs")
        .insert([{
          user_id: user.id,
          company_id: memberData?.company_id || null,
          action_type: actionType,
          description,
          metadata,
        }]);

      if (error) {
        console.error("Failed to log activity:", error);
      }
    } catch (err) {
      console.error("Error logging activity:", err);
    }
  }, []);

  return { logActivity };
};

// Standalone function for use outside of React components
export const logActivityDirect = async ({ 
  userId, 
  companyId, 
  actionType, 
  description, 
  metadata = {} 
}: {
  userId: string;
  companyId?: string | null;
  actionType: ActivityActionType;
  description: string;
  metadata?: Json;
}) => {
  try {
    const { error } = await supabase
      .from("user_activity_logs")
      .insert([{
        user_id: userId,
        company_id: companyId || null,
        action_type: actionType,
        description,
        metadata,
      }]);

    if (error) {
      console.error("Failed to log activity:", error);
    }
  } catch (err) {
    console.error("Error logging activity:", err);
  }
};